<template>
  <div>
    <div class="py-5 flex items-center gap-4 mb-4 justify-between filter-container">
      <div class="flex items-center gap-4">
        <form @submit.prevent="submitSearch()">
          <vs-input
            icon-no-border
            placeholder="Search"
            v-model="searchQuery"
            class="input-square-full"
            icon="icon-search"
            icon-pack="feather"
          />
        </form>
        <div class="flex-grow site-select-wrapper">
          <v-select
            placeholder="Select site"
            class="w-full"
            v-model="selectedSite"
            append-to-body
            :options="siteOptionLists"
            label="name"
            @input="onSiteChange"
          />
        </div>
        <div class="flex-grow site-select-wrapper">
          <v-select
            placeholder="Select area"
            class="w-full"
            v-model="selectedArea"
            :options="areaOptionLists"
            :disabled="!selectedSite"
            label="name"
            @input="onAreaChange"
          />
        </div>
      </div>
    </div>
    <vs-table stripe :data="lists">
      <template slot="thead">
        <vs-th @click="toggleSort('id')" class="cursor-pointer">
          <div class="flex items-center justify-center">
            <span>No</span>
          </div>
        </vs-th>
        <vs-th @click="toggleSort('name')" class="text-left cursor-pointer">
          <div class="flex items-center">
            <span>Camera Name</span>
            <div
              class="ml-4 flex flex-col sort-icon-group"
              @click.stop="toggleSort('name')"
              style="cursor: pointer"
            >
              <font-awesome-icon
                :icon="['fas', 'caret-up']"
                :class="getSortClass('name', 'asc')"
              />
              <font-awesome-icon
                :icon="['fas', 'caret-down']"
                :class="getSortClass('name', 'desc')"
              />
            </div>
          </div>
        </vs-th>
        <vs-th
          @click="toggleSort('ipAddress')"
          class="text-left cursor-pointer"
        >
          <div class="flex items-center">
            <span>IP Address</span>
            <div
              class="ml-4 flex flex-col sort-icon-group"
              @click.stop="toggleSort('ipAddress')"
              style="cursor: pointer"
            >
              <font-awesome-icon
                :icon="['fas', 'caret-up']"
                :class="getSortClass('ipAddress', 'asc')"
              />
              <font-awesome-icon
                :icon="['fas', 'caret-down']"
                :class="getSortClass('ipAddress', 'desc')"
              />
            </div>
          </div>
        </vs-th>
        <vs-th @click="toggleSort('areaName')" class="text-left cursor-pointer">
          <div class="flex items-center">
            <span>Area</span>
            <div
              class="ml-4 flex flex-col sort-icon-group"
              @click.stop="toggleSort('areaName')"
              style="cursor: pointer"
            >
              <font-awesome-icon
                :icon="['fas', 'caret-up']"
                :class="getSortClass('areaName', 'asc')"
              />
              <font-awesome-icon
                :icon="['fas', 'caret-down']"
                :class="getSortClass('areaName', 'desc')"
              />
            </div>
          </div>
        </vs-th>
        <vs-th @click="toggleSort('siteName')" class="text-left cursor-pointer">
          <div class="flex items-center">
            <span>Site</span>
            <div
              class="ml-4 flex flex-col sort-icon-group"
              @click.stop="toggleSort('siteName')"
              style="cursor: pointer"
            >
              <font-awesome-icon
                :icon="['fas', 'caret-up']"
                :class="getSortClass('siteName', 'asc')"
              />
              <font-awesome-icon
                :icon="['fas', 'caret-down']"
                :class="getSortClass('siteName', 'desc')"
              />
            </div>
          </div>
        </vs-th>
        <vs-th class="text-left">Event</vs-th>

        <vs-th class="text-center">Action</vs-th>
      </template>

      <template slot-scope="{ data }">
        <vs-tr class="pln-color" v-for="(tr, indextr) in data" :key="indextr">
          <vs-td>{{ (currentPage - 1) * itemsPerPage + indextr + 1 }}</vs-td>
          <vs-td class="text-left">{{ tr.name }} ({{ tr.camName }})</vs-td>
          <vs-td class="text-left">{{ tr.ipAddress }}</vs-td>
          <vs-td class="text-left">{{ tr.areaName }}</vs-td>
          <vs-td class="text-left">{{ tr.siteName }}</vs-td>
          <vs-td class="text-left">{{ tr.eventType ? eventType : "-" }}</vs-td>

          <vs-td class="text-center">
            <div class="flex justify-center gap-mini">
              <vs-tooltip text="Edit">
                <a href="" @click.prevent="gotoUpdateCameraForm(tr)">
                  <feather-icon icon="EditIcon" size="20" />
                </a>
              </vs-tooltip>
              <vs-tooltip text="Link">
                <a href="" @click.prevent="showRecordAudioModal(tr)">
                  <feather-icon icon="LinkIcon" size="20" />
                </a>
              </vs-tooltip>
            </div>
          </vs-td>
        </vs-tr>
      </template>
    </vs-table>

    <!-- pagination -->
    <div class="pagination-container">
      <div class="pagination-left">
        <span class="mr-2">Show data</span>
        <vs-select
          v-model="itemsPerPage"
          @input="onItemsPerPageChange"
          class="page-select"
        >
          <vs-select-item
            v-for="(option, index) in pageOptions"
            :key="index"
            :value="option.value"
            :text="option.text"
          />
        </vs-select>
      </div>

      <CustomPagination
        :current-page.sync="currentPage"
        :total-pages="totalPages"
        :total-data="totalData"
        @update:currentPage="getCameraLists"
      />
    </div>

    <!-- Clean Modal -->
    <vs-popup
      class="clean-modal"
      title="Open Camera Viewer"
      :active.sync="showModalAudio"
    >
      <div class="modal-content">
        <p class="modal-desc">Open camera viewer for:</p>

        <!-- Camera Info -->
        <div class="camera-info">
          <strong>{{ selectedName }}</strong> - {{ selectedId }}
        </div>

        <!-- Link Section -->
        <div class="link-section">
          <!-- Regular Camera Link -->
          <div class="link-group">
            <label class="link-label">Regular Camera Viewer Link:</label>
            <div class="link-copy-container">
              <input
                type="text"
                :value="cameraViewerUrl"
                readonly
                class="link-input"
                aria-label="Camera viewer link"
              />
              <vs-button
                @click="copyCameraLink('regular')"
                class="copy-btn"
                color="success"
              >
                Copy
              </vs-button>
            </div>
          </div>

          <!-- iOS Camera Link -->
          <div class="link-group">
            <label class="link-label">iOS Camera Viewer Link:</label>
            <div class="link-copy-container">
              <input
                type="text"
                :value="cameraViewerIosUrl"
                readonly
                class="link-input"
                aria-label="iOS camera viewer link"
              />
              <vs-button
                @click="copyCameraLink('ios')"
                class="copy-btn"
                color="primary"
              >
                Copy
              </vs-button>
            </div>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import api from "@/api";
import CustomPagination from "@/components/CustomPagination.vue";
import vSelect from "vue-select";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCaretUp, faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

library.add(faCaretUp, faCaretDown);

export default {
  components: {
    CustomPagination,
    vSelect,
    "font-awesome-icon": FontAwesomeIcon,
  },
  data() {
    return {
      selectedId: "",
      selectedName: "",
      searchQuery: "",
      lists: [],
      itemsPerPage: 10,
      pageOptions: [
        { value: 10, text: "10" },
        { value: 25, text: "25" },
        { value: 50, text: "50" },
        { value: 100, text: "100" },
      ],
      currentPage: 1,
      totalData: 0,
      totalPages: 0,
      showModalAudio: false,
      newAudio: null,
      recorder: null,
      selectedSite: null,
      selectedArea: null,
      siteOptionLists: [],
      areaOptionLists: [],
      sortField: "",
      sortOrder: "asc",
      iosPlayerUrl: "",
    };
  },
  computed: {
    cameraViewerUrl() {
      try {
        const origin = window.location.origin || "";
        return `${origin}/camera.html?cameraId=${encodeURIComponent(
          this.selectedId
        )}`;
      } catch {
        return `/camera.html?cameraId=${encodeURIComponent(this.selectedId)}`;
      }
    },
    cameraViewerIosUrl() {
      // Return the converted player URL if available, otherwise return empty or fallback
      return this.iosPlayerUrl || "Processing stream...";
    },
  },
  mounted() {
    this.getSiteOptions();
    this.getCameraLists();
  },
  methods: {
    getSiteOptions() {
      api
        .siteOption({})
        .then((result) => result.data)
        .then((data) => {
          console.log("✅ [getSiteOptions] Response:", data);
          if (data.status && data.data) {
            this.siteOptionLists = data.data;
          }
        })
        .catch((e) => {
          console.error("❌ [getSiteOptions] Error:", e);
        });
    },
    onSiteChange() {
      console.log("🔄 [onSiteChange] Selected site:", this.selectedSite);
      this.selectedArea = null;
      this.areaOptionLists = [];
      this.currentPage = 1;

      if (this.selectedSite && this.selectedSite.id) {
        this.getAreaOptions();
      }
      this.getCameraLists();
    },
    getAreaOptions() {
      if (!this.selectedSite || !this.selectedSite.id) return;

      api
        .areaOption({ siteId: this.selectedSite.id })
        .then((result) => result.data)
        .then((data) => {
          console.log("✅ [getAreaOptions] Response:", data);
          if (data.status && data.data) {
            this.areaOptionLists = data.data;
          }
        })
        .catch((e) => {
          console.error("❌ [getAreaOptions] Error:", e);
        });
    },
    onAreaChange() {
      console.log("🔄 [onAreaChange] Selected area:", this.selectedArea);
      this.currentPage = 1;
      this.getCameraLists();
    },
    submitSearch() {
      this.getCameraLists();
    },
    onItemsPerPageChange() {
      this.currentPage = 1;
      this.getCameraLists();
    },
    toggleSort(field) {
      console.log("🔽 [toggleSort] Clicked field:", field);

      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortField = field;
        this.sortOrder = "asc";
      }

      console.log("🔄 [toggleSort] New sortField:", this.sortField);
      console.log("🔄 [toggleSort] New sortOrder:", this.sortOrder);

      this.getCameraLists();
    },
    getSortClass(field, direction) {
      const isActive = this.sortField === field && this.sortOrder === direction;
      return {
        "sort-icon": true,
        active: isActive,
      };
    },
    getCameraLists() {
      this.$vs.loading();
      const params = {
        search: this.searchQuery,
        page: this.currentPage,
        limit: this.itemsPerPage,
      };

      if (this.selectedSite && this.selectedSite.id) {
        params.siteId = this.selectedSite.id;
      }

      if (this.selectedArea && this.selectedArea.id) {
        params.areaId = this.selectedArea.id;
      }

      if (this.sortField) {
        params.sort = `${this.sortField} ${this.sortOrder}`;
      }

      console.log("📦 [getCameraLists] API Params:", params);

      api
        .cameras(params)
        .then((result) => result.data)
        .then((data) => {
          if (data && data.status) {
            this.lists = Array.isArray(data.data) ? data.data : [];
            this.currentPage = Number(data.page) || this.currentPage;
            this.itemsPerPage = Number(data.limit) || this.itemsPerPage;
            this.totalData = Number(data.totalData) || this.totalData;
            this.totalPages = Number(data.maxPage) || this.totalPages;
          }
          this.$vs.loading.close();
        })
        .catch((e) => {
          console.log(e);
          this.$vs.loading.close();
        });
    },
    inArray(needle, haystack) {
      return haystack.includes(needle);
    },
    gotoUpdateCameraForm(data) {
      console.log("🔗 [gotoUpdateCameraForm] Navigating with camera data:", data)
      this.$store.dispatch("updateCurrentCamera", data);
      this.$router.push("/org_management/update_camera");
    },
    showRecordAudioModal(data) {
      this.selectedId = data.id;
      this.selectedName = data.name || data.camName || "camera";
      this.showModalAudio = true;
      this.loadIosStreamUrl();
    },
    loadIosStreamUrl() {
      // Generate static iOS player URL with camera ID
      const iosUrl = `http://124.158.175.84:2881/livestream/iOS/${this.selectedId}`;
      this.$set(this, "iosPlayerUrl", iosUrl);
    },
    closeModalAudio() {
      this.selectedId = "";
      this.selectedName = "";
      this.showModalAudio = false;
    },
    async copyCameraLink(type = "regular") {
      const url =
        type === "ios" ? this.cameraViewerIosUrl : this.cameraViewerUrl;
      try {
        // Try modern Clipboard API first (requires HTTPS or localhost)
        if (navigator.clipboard && navigator.clipboard.writeText) {
          try {
            await navigator.clipboard.writeText(url);
          } catch (clipboardError) {
            // Clipboard API failed, fall back to textarea method
            this.copyUsingTextarea(url);
            throw new Error("clipboard-fallback");
          }
        } else {
          // Older browsers: use textarea fallback
          this.copyUsingTextarea(url);
          throw new Error("clipboard-unsupported");
        }

        this.$vs.notify({
          title: "Copied",
          text: `${
            type === "ios" ? "iOS" : "Regular"
          } camera link copied to clipboard`,
          color: "success",
        });
      } catch (e) {
        // If we already tried textarea and it worked, don't show error
        if (
          e.message !== "clipboard-fallback" &&
          e.message !== "clipboard-unsupported"
        ) {
          this.$vs.notify({
            title: "Error",
            text: "Unable to copy link",
            color: "danger",
          });
        }
      }
    },

    copyUsingTextarea(url) {
      const el = document.createElement("textarea");
      el.value = url;
      el.style.position = "fixed";
      el.style.left = "-9999px";
      el.style.top = "-9999px";
      document.body.appendChild(el);
      el.focus();
      el.select();
      try {
        document.execCommand("copy");
      } finally {
        document.body.removeChild(el);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.icon {
  margin: 0 5px;
}
.site-select-wrapper {
  min-width: 350px;
}
.text-center:deep(.vs-table-text) {
  justify-content: center;
}
.text-right:deep(.vs-table-text) {
  justify-content: flex-end;
}
.action-icons {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
}
.pagination-left {
  display: flex;
  align-items: center;
}
.page-select {
  width: 100px;
}
::v-deep .vs-con-select {
  width: 100%;
}
::v-deep .vs-select__selections {
  overflow: visible !important;
  white-space: normal;
}
.cursor-pointer {
  cursor: pointer;
}
.sort-icon-group {
  line-height: 0.7;
}
.sort-icon {
  color: white;
  font-size: 12px;
  transition: opacity 0.2s ease;
}
.sort-icon.active {
  opacity: 1;
  color: #009fb2;
}

/* --- Clean Modal Styles --- */
.clean-modal {
  .modal-content {
    padding: 1rem 0;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .modal-desc {
    margin: 0;
    color: #6b7280;
    font-size: 0.95rem;
  }

  .camera-info {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 12px 16px;
    font-size: 0.95rem;
    color: #374151;

    strong {
      color: #111827;
    }
  }

  .link-section {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .link-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .link-label {
    font-size: 0.9rem;
    color: #374151;
    font-weight: 500;
  }

  .link-copy-container {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
  }

  .copy-btn {
    padding: 0.5rem 1rem;
    white-space: nowrap;
  }

  .link-input {
    flex: 1;
    padding: 10px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #ffffff;
    font-size: 0.9rem;
    color: #111827;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: #3b82f6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &:read-only {
      background-color: #f9fafb;
      cursor: not-allowed;
    }
  }

  .modal-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    margin-top: 1rem;

    .open-btn,
    .close-btn {
      min-width: 120px;
      height: 38px;
      padding: 0.5rem 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.9rem;
    }
  }
}

// Adjust Vuesax modal header
:deep(.vs-popup--header) {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 1rem 1.5rem;

  h3 {
    color: #111827;
    font-weight: 600;
    margin: 0;
  }
}

:deep(.vs-popup) {
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

:deep(.con-vs-popup .vs-popup--content) {
  padding: 1.5rem;
}
</style>
